import { extractProperty } from "@/utils"
import { InitialCategoryData, ProcessedItemsData } from "./types"

/**
 * Gets valid categories by filtering out empty/falsy values
 */
export const getValidCategories = (categories: string[]): string[] => {
  return Array.isArray(categories) ? categories.filter(Boolean) : []
}

/**
 * Determines initial category selection based on available categories
 * Returns the first category as selected if there are multiple categories
 */
export const getInitialCategoryData = (
  validCategories: string[],
): InitialCategoryData => {
  if (validCategories.length > 1) {
    return {
      selectedCategory: validCategories[0],
      value: 0,
    }
  }
  return {
    selectedCategory: null,
    value: null,
  }
}

/**
 * Filters items based on search term
 * Searches in title, itemName, name, and link name fields
 */
const applySearchFilter = (items: any[], searchTerm: string): any[] => {
  if (!searchTerm) return items

  const searchLower = searchTerm.toLowerCase()
  return items.filter((item: any) => {
    const title =
      extractProperty(item, "title")?.toLowerCase() ||
      item?.itemName?.toLowerCase() ||
      item?.name?.toLowerCase() ||
      ""
    const linkName = item?.link?.name?.toLowerCase() || ""
    return title.includes(searchLower) || linkName.includes(searchLower)
  })
}

/**
 * Filters items by selected category
 */
const applyCategoryFilter = (items: any[], selectedCategory: string): any[] => {
  return items.filter((item: any) => {
    return (
      item?.filter?.includes?.(selectedCategory) ||
      extractProperty(item, "filter") ||
      item?.category?.includes?.(selectedCategory)
    )
  })
}

/**
 * Groups items by their categories
 */
const groupItemsByCategory = (items: any[]): Record<string, any[]> => {
  const grouped: Record<string, any[]> = {}

  items.forEach((item) => {
    const itemCategories =
      item?.filter || extractProperty(item, "filter") || item?.category || []

    // An item might belong to multiple categories
    if (Array.isArray(itemCategories)) {
      itemCategories.forEach((category) => {
        if (!grouped[category]) {
          grouped[category] = []
        }
        grouped[category].push(item)
      })
    } else if (typeof itemCategories === "string") {
      if (!grouped[itemCategories]) {
        grouped[itemCategories] = []
      }
      grouped[itemCategories].push(item)
    }
  })

  return grouped
}

/**
 * Main function to process items data based on filters and grouping options
 */
export const processItemsData = (
  itemsList: any[],
  searchTerm: string,
  selectedCategory: string | null,
  allFilterCategory: string | undefined,
  validCategories: string[],
  groupByCategory: boolean,
): ProcessedItemsData => {
  // Ensure itemsList is an array
  const items = Array.isArray(itemsList) ? itemsList : []

  // Apply search filter first
  let filteredItems = applySearchFilter(items, searchTerm)

  let groupedItems: Record<string, any[]> = {}

  // Only apply category filtering if there are multiple categories
  if (validCategories.length > 1) {
    // If a specific category is selected (not "all" category), filter by that category
    if (selectedCategory && selectedCategory !== allFilterCategory) {
      filteredItems = applyCategoryFilter(filteredItems, selectedCategory)
      groupedItems = {}
    } else if (groupByCategory) {
      // If no specific category selected but groupByCategory is enabled, group the results
      groupedItems = groupItemsByCategory(filteredItems)
      filteredItems = []
    } else {
      // Default behavior when multiple categories exist - show all filtered items
      groupedItems = {}
    }
  } else {
    // When there's only one category or no categories, show all items without category filtering
    groupedItems = {}
  }

  return { filteredItems, groupedItems }
}
